import { useSessionStore } from 'app/stores/globalStore';

export class ModuleDisabledError extends Error {
  code = 'module.disabled';

  constructor() {
    super('Module disabled');
    this.name = 'ModuleDisabledError';
  }
}

export class PermissionDeniedError extends Error {
  code = 'permission.denied';
  required: string[];

  constructor(required: string[]) {
    super('Permission denied');
    this.name = 'PermissionDeniedError';
    this.required = required;
  }
}

export class ConflictError extends Error {
  code: string;
  body: unknown;

  constructor(code: string, body: unknown) {
    super('Conflict');
    this.name = 'ConflictError';
    this.code = code;
    this.body = body;
  }
}

export class ApiError extends Error {
  code: string;
  status: number;

  constructor(code: string, status: number) {
    super(`Request failed with status ${status}`);
    this.name = 'ApiError';
    this.code = code;
    this.status = status;
  }
}

interface ErrorResponseBody {
  code?: string;
  required?: string[];
}

const parseErrorBody = async (response: Response): Promise<ErrorResponseBody> => {
  try {
    return (await response.json()) as ErrorResponseBody;
  } catch {
    return {};
  }
};

export const buildIdentityHeaders = (token: string): Record<string, string> => {
  const headers: Record<string, string> = {};
  if (token) headers.Authorization = `Bearer ${token}`;
  const userId = useSessionStore.getState().loginUser?.agentId;
  if (userId) headers['X-Okron-User-Id'] = userId;
  return headers;
};

export const handleUnauthorized = (): void => {
  useSessionStore.getState().setLoginUser(undefined);
  window.location.href = '/';
};

export async function authFetch<T>(
  url: string,
  options: RequestInit = {}
): Promise<T> {
  const token = useSessionStore.getState().loginUser?.token;

  const headers: HeadersInit = {
    ...(options.body ? { 'Content-Type': 'application/json' } : {}),
    ...buildIdentityHeaders(token ?? ''),
    ...options.headers,
  };

  const response = await fetch(url, { ...options, headers });

  if (response.status === 401) {
    handleUnauthorized();
    throw new ApiError('unauthorized', 401);
  }

  if (response.status === 403) {
    const body = await parseErrorBody(response);
    if (body.code === 'module.disabled') throw new ModuleDisabledError();
    if (body.code === 'permission.denied') {
      throw new PermissionDeniedError(body.required ?? []);
    }
    throw new ApiError(body.code ?? 'forbidden', 403);
  }

  if (response.status === 409) {
    const body = await parseErrorBody(response);
    throw new ConflictError(body.code ?? 'conflict', body);
  }

  if (!response.ok) {
    const body = await parseErrorBody(response);
    throw new ApiError(body.code ?? String(response.status), response.status);
  }

  if (response.status === 204) {
    return undefined as unknown as T;
  }

  const contentType = response.headers.get('content-type');
  if (!contentType?.includes('application/json')) {
    return undefined as unknown as T;
  }

  return response.json();
}

export async function authFetchWithStatus<T>(
  url: string,
  options: RequestInit = {}
): Promise<{ data: T; status: number }> {
  const token = useSessionStore.getState().loginUser?.token;

  const headers: HeadersInit = {
    ...(options.body ? { 'Content-Type': 'application/json' } : {}),
    ...buildIdentityHeaders(token ?? ''),
    ...options.headers,
  };

  const response = await fetch(url, { ...options, headers });

  if (response.status === 401) {
    handleUnauthorized();
    throw new ApiError('unauthorized', 401);
  }

  if (response.status === 403) {
    const body = await parseErrorBody(response);
    if (body.code === 'module.disabled') throw new ModuleDisabledError();
    if (body.code === 'permission.denied') {
      throw new PermissionDeniedError(body.required ?? []);
    }
    throw new ApiError(body.code ?? 'forbidden', 403);
  }

  if (response.status === 409) {
    const body = await parseErrorBody(response);
    throw new ConflictError(body.code ?? 'conflict', body);
  }

  if (!response.ok) {
    const body = await parseErrorBody(response);
    throw new ApiError(body.code ?? String(response.status), response.status);
  }

  if (response.status === 204) {
    return { data: undefined as unknown as T, status: response.status };
  }

  const contentType = response.headers.get('content-type');
  if (!contentType?.includes('application/json')) {
    return { data: undefined as unknown as T, status: response.status };
  }

  const data = (await response.json()) as T;
  return { data, status: response.status };
}

const ERROR_CODE_TO_I18N_KEY: Record<string, string> = {
  'module.disabled': 'module.disabled',
  'permission.denied': 'permission.denied',
};

const GENERIC_ERROR_I18N_KEY = 'error.contact.admin';

export const errorCodeToI18nKey = (code: string): string =>
  ERROR_CODE_TO_I18N_KEY[code] ?? GENERIC_ERROR_I18N_KEY;
